import {
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * A small set of presentational primitives. Styling follows the brand tokens:
 * deep green (#017A00) for text and actions, primary green (#22C100) for fills
 * and accents only. See apps/web/src/styles/brand-tokens.css.
 */

export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-surface-muted bg-white shadow-sm ${className}`}>
      {children}
    </section>
  );
}

export function CardBody({ children }: { children: ReactNode }) {
  return <div className="p-4 sm:p-5">{children}</div>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-xs font-bold uppercase tracking-widest text-brand-deep">{eyebrow}</p>
        ) : null}
        <h1 className="mt-1 text-xl font-bold text-ink sm:text-2xl">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-sm font-light text-ink-body">{description}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex w-full shrink-0 flex-wrap items-center gap-2 sm:w-auto">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

type ButtonTone = 'primary' | 'outline' | 'ghost' | 'danger';

const BUTTON_TONES: Record<ButtonTone, string> = {
  primary: 'bg-brand text-white hover:bg-[color:var(--gs-primary-hover)] border-transparent',
  outline: 'bg-white text-brand-deep border-brand-deep hover:bg-brand-deep hover:text-white',
  ghost: 'bg-white text-ink-body border-surface-border hover:border-brand',
  danger: 'bg-white text-red-700 border-red-300 hover:bg-red-50',
};

export function Button({
  children,
  onClick,
  type = 'button',
  tone = 'primary',
  disabled = false,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  tone?: ButtonTone;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-11 items-center justify-center gap-2 rounded-full border px-4 text-xs font-bold uppercase tracking-wide transition disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 ${BUTTON_TONES[tone]}`}
    >
      {children}
    </button>
  );
}

/** The only element types it makes sense to attach a form name to. */
const FORM_CONTROL_TAGS = new Set(['input', 'select', 'textarea']);

function slug(value: string): string {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || 'field';
}

/**
 * An explanation for a term a newcomer would not know, revealed on hover or
 * keyboard focus.
 *
 * Deliberately CSS-only: the trigger is a real `<button type="button">` (so it
 * is focusable and does not submit the form it sits in), and the panel is kept
 * in the DOM at all times, hidden with opacity, and wired up with
 * `aria-describedby`. That means a screen reader reads the explanation even
 * while it is visually hidden, which is the whole point of it.
 *
 * Never place one of these inside a `<label>`: a nested button is the first
 * labelable descendant, so it would steal the label's association from the
 * field it is meant to describe. `Field` renders it as a sibling for that
 * reason; the call sites that place it beside a standalone checkbox do the same.
 */
export function Tooltip({ text, subject }: { text: string; subject?: string }) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  /**
   * Place the panel from the trigger's coordinates, clamped to the viewport.
   *
   * It is `position: fixed`, so a panel near the right edge can never widen the
   * page and bring in a horizontal scrollbar — an absolutely positioned one did,
   * visibly, at both 1280px and on a phone. Showing it is still CSS-only; this
   * only decides where it sits.
   */
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const half = 128; // w-64 / 2
    const margin = 8;
    const rect = trigger.getBoundingClientRect();
    const centre = rect.left + rect.width / 2;
    const viewport = document.documentElement.clientWidth || window.innerWidth;
    const left = Math.min(Math.max(centre, half + margin), viewport - half - margin);
    setPos({ left, top: rect.top - margin });
  }, []);

  return (
    <span className="group relative inline-flex align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label={subject ? `What does ${subject} mean?` : 'What does this mean?'}
        aria-describedby={id}
        onFocus={reposition}
        onMouseEnter={reposition}
        className="flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-surface-border bg-white text-[10px] font-bold leading-none text-ink-muted transition hover:border-brand hover:text-brand-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-soft"
      >
        ?
      </button>
      <span
        id={id}
        role="tooltip"
        style={pos ? { left: pos.left, top: pos.top, transform: 'translate(-50%, -100%)' } : undefined}
        className="pointer-events-none fixed z-30 w-64 rounded-md border border-surface-muted bg-white px-3 py-2 text-xs font-normal normal-case leading-snug tracking-normal text-ink-body opacity-0 shadow-lg transition-opacity duration-100 group-focus-within:opacity-100 group-hover:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

/**
 * A labelled field.
 *
 * The wrapping <label> already associates the text with the control for screen
 * readers, but browsers also expect a `name` (for autofill and form semantics).
 * Rather than repeat it at ~40 call sites — and forget it at the next one — the
 * name is derived from the label and injected into the control.
 *
 * The optional `tooltip` explains the concept itself, as opposed to `hint`,
 * which says what this particular value should be. It sits below the control and
 * *outside* the label, so the label keeps associating with the control.
 */
export function Field({
  label,
  hint,
  tooltip,
  children,
}: {
  label: string;
  hint?: string;
  tooltip?: string;
  children: ReactNode;
}) {
  const control =
    isValidElement(children) &&
    typeof children.type === 'string' &&
    FORM_CONTROL_TAGS.has(children.type) &&
    !(children.props as { name?: string }).name
      ? cloneElement(children as ReactElement<{ name?: string }>, { name: slug(label) })
      : children;

  return (
    <div className="block">
      <label className="block">
        <span className="mb-1 block text-sm font-bold text-ink">{label}</span>
        {control}
      </label>
      {hint || tooltip ? (
        <span className="mt-1 flex items-start gap-1.5 text-xs text-ink-muted">
          {hint ? <span>{hint}</span> : null}
          {tooltip ? <Tooltip text={tooltip} subject={label} /> : null}
        </span>
      ) : null}
    </div>
  );
}

export const inputClass =
  'min-h-[44px] w-full rounded-sm border border-surface-border bg-white px-3 py-2 text-base text-ink-body outline-none transition focus:border-brand focus:ring-2 focus:ring-brand-soft sm:min-h-0 sm:text-sm';

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      {children}
    </div>
  );
}

export function InfoNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border-l-4 border-accent bg-accent-soft px-4 py-3 text-sm text-ink-body">
      {children}
    </div>
  );
}

type BadgeTone = 'green' | 'accent' | 'muted' | 'hazard' | 'dark';

const BADGE_TONES: Record<BadgeTone, string> = {
  green: 'bg-brand-soft text-brand-deep',
  accent: 'bg-accent-soft text-accent',
  muted: 'bg-surface text-ink-muted',
  hazard: 'bg-[color:var(--gs-hazard)]/20 text-[color:#8a5a00]',
  dark: 'bg-ink text-white',
};

export function Badge({ tone = 'muted', children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-surface-muted">
      <table className="w-full border-collapse bg-white text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={`bg-brand-forest px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-white ${className}`}
    >
      {children}
    </th>
  );
}

export function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <td className={`border-b border-surface-muted px-4 py-3 align-top text-ink-body ${className}`}>
      {children}
    </td>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="px-4 py-8 text-center text-sm text-ink-muted">{children}</p>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 p-6 text-sm text-ink-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-surface-border border-t-brand" />
      {label}
    </div>
  );
}
